<template>
    <section class="qp-card p-5 sm:p-7">
        <div class="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
            <!-- Identity + price -->
            <div class="flex items-start gap-4 sm:gap-6 min-w-0">
                <div class="shrink-0">
                    <img v-if="headerData?.lgo && !logoFailed" :src="headerData.lgo" :alt="symbol"
                        @error="logoFailed = true"
                        class="h-[56px] w-[56px] sm:h-[72px] sm:w-[72px] rounded-2xl object-contain bg-white ring-1 ring-line-card p-1.5" />
                    <span v-else
                        class="h-[56px] w-[56px] sm:h-[72px] sm:w-[72px] rounded-2xl bg-surface-3 ring-1 ring-line-card text-[24px] sm:text-[28px] font-semibold text-ink-2 inline-flex items-center justify-center">
                        {{ symbol ? symbol[0] : "" }}
                    </span>
                </div>

                <div class="min-w-0">
                    <h1 class="text-[22px] sm:text-[28px] leading-tight font-semibold tracking-[-0.02em] text-ink line-clamp-2">
                        {{ headerData?.name || symbol }}
                    </h1>
                    <div class="flex flex-wrap items-center gap-1.5 mt-2">
                        <span class="qp-chip qp-chip-strong">{{ symbol }}</span>
                        <span v-if="exchangeShort" class="qp-chip" :title="headerData?.exchange">{{ exchangeShort }}</span>
                        <span v-if="headerData?.gsector" class="qp-chip">{{ headerData.gsector }}</span>
                    </div>

                    <div class="flex flex-wrap items-baseline gap-x-3 gap-y-2 mt-4">
                        <span class="qp-num text-[36px] sm:text-[44px] leading-none font-semibold tracking-[-0.03em] text-ink">
                            {{ fmt.price(price?.currentPrice) }}
                        </span>
                        <span class="text-[14px] font-medium text-muted">{{ currency }}</span>
                        <span v-if="hasChange" class="qp-pill qp-num"
                            :class="`qp-pill-${changeTone}`">
                            <svg v-if="changeTone !== 'flat'" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"
                                :class="changeTone === 'down' ? 'rotate-180' : ''">
                                <path d="M5 1.5 9 8H1z" fill="currentColor" />
                            </svg>
                            {{ fmt.signed(price?.priceChange) }} ({{ fmt.pct(price?.priceChangePercent, 2, { signed: true }) }})
                        </span>
                    </div>
                    <div class="text-[12px] text-muted mt-2">
                        {{ $t("As of") }} {{ fmt.marketTime(price?.timestamp) }}
                    </div>
                </div>
            </div>

            <!-- Sweet Spot Score -->
            <button v-if="hasOss" type="button" @click="$emit('openOptions')"
                class="group text-left shrink-0 flex items-center gap-4 rounded-2xl border border-line-card bg-gradient-to-br from-surface to-brand-tint px-4 py-3.5 hover:border-brand/40 hover:shadow-[0_6px_20px_rgba(0,170,111,0.10)] transition lg:min-w-[280px]">
                <div class="relative h-[64px] w-[64px] shrink-0">
                    <svg viewBox="0 0 64 64" class="h-full w-full -rotate-90" aria-hidden="true">
                        <circle cx="32" cy="32" r="27" fill="none" class="stroke-line-soft" stroke-width="6" />
                        <circle cx="32" cy="32" r="27" fill="none" :class="ossColor" stroke-width="6" stroke-linecap="round"
                            :stroke-dasharray="gaugeCircumference" :stroke-dashoffset="gaugeOffset" />
                    </svg>
                    <span class="qp-num absolute inset-0 flex items-center justify-center text-[18px] font-semibold text-ink">
                        {{ fmt.num(oss.oss, 0) }}
                    </span>
                </div>
                <div class="min-w-0">
                    <div class="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                        {{ $t("Sweet Spot Score") }}
                    </div>
                    <div class="text-[14px] font-semibold mt-0.5" :style="{ color: ossColor }">{{ $t(ossLabel) }}</div>
                    <div v-if="hasBestExpiration" class="text-[12px] text-muted mt-0.5 qp-num">
                        {{ $t("Best expiration") }} {{ fmt.date(oss.bestExpiration, { month: "short", day: "numeric" }) }}
                        <span v-if="oss.bestDte != null">· {{ oss.bestDte }} {{ $t("DTE") }}</span>
                    </div>
                    <div class="text-[12px] font-medium text-brand mt-1 group-hover:underline">
                        {{ $t("View options") }} →
                    </div>
                </div>
            </button>
        </div>

        <!-- Quick stats -->
        <dl class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-5 mt-6 pt-5 border-t border-line-soft">
            <div>
                <dt class="qp-label">{{ $t("Open") }}</dt>
                <dd class="qp-stat">{{ fmt.money(price?.openingPrice) }}</dd>
            </div>
            <div>
                <dt class="qp-label">{{ $t("Prev. close") }}</dt>
                <dd class="qp-stat">{{ fmt.money(price?.previousClosePrice) }}</dd>
            </div>
            <div>
                <dt class="qp-label">{{ $t("Day range") }}</dt>
                <dd class="qp-stat">{{ fmt.price(price?.lowPrice) }} – {{ fmt.price(price?.highPrice) }}</dd>
                <div v-if="dayPos !== null" class="qp-range"><span :style="{ left: dayPos + '%' }"></span></div>
            </div>
            <div>
                <dt class="qp-label">{{ $t("52-week range") }}</dt>
                <dd class="qp-stat">{{ fmt.price(yearLow) }} – {{ fmt.price(yearHigh) }}</dd>
                <div v-if="yearPos !== null" class="qp-range"><span :style="{ left: yearPos + '%' }"></span></div>
            </div>
            <div>
                <dt class="qp-label">{{ $t("Market Cap") }}</dt>
                <dd class="qp-stat">{{ marketCap }}</dd>
            </div>
            <div>
                <dt class="qp-label">{{ $t("Volume") }}</dt>
                <dd class="qp-stat">{{ fmt.compact(extraData?.volume) }}</dd>
                <div v-if="priceMetricsData?._10DayAverageTradingVolume" class="text-[11px] text-faint mt-0.5 qp-num">
                    {{ $t("Avg 10D") }} {{ fmt.compact(priceMetricsData._10DayAverageTradingVolume) }}
                </div>
            </div>
        </dl>
    </section>
</template>

<script>
import { formatMixin, moneyCompact, isNum } from "@/utils/format";

const CIRCUMFERENCE = 2 * Math.PI * 27;

export default {
    name: "QuoteHeader",
    mixins: [formatMixin],
    props: {
        headerData: { type: Object, default: () => ({}) },
        price: { type: Object, default: () => ({}) },
        symbol: { type: String, default: "" },
        metricData: { type: Object, default: () => ({}) },
        priceMetricsData: { type: Object, default: () => ({}) },
        extraData: { type: Object, default: () => ({}) },
        oss: { type: Object, default: null },
    },
    data() {
        return { logoFailed: false };
    },
    computed: {
        hasChange() {
            return isNum(this.price?.priceChange);
        },
        changeTone() {
            return this.fmt.tone(this.price?.priceChange);
        },
        exchangeShort() {
            const ex = (this.headerData?.exchange || "").toUpperCase();
            if (!ex) return "";
            if (ex.includes("NASDAQ")) return "NASDAQ";
            if (ex.includes("NYSE ARCA")) return "NYSE Arca";
            if (ex.includes("NYSE AMERICAN") || ex.includes("AMERICAN")) return "NYSE American";
            if (ex.includes("NEW YORK") || ex.includes("NYSE")) return "NYSE";
            if (ex.includes("OTC")) return "OTC";
            if (ex.includes("TORONTO")) return "TSX";
            return this.headerData.exchange;
        },
        marketCap() {
            const mc = this.headerData?.marketCapitalization;
            // Finnhub reports market capitalisation in millions.
            return isNum(mc) ? moneyCompact(Number(mc) * 1e6) : this.fmt.DASH;
        },
        dayPos() {
            return this.fmt.rangePosition(this.price?.currentPrice, this.price?.lowPrice, this.price?.highPrice);
        },
        // The provider's 52-week range lags a day: widen it with today's session
        // so a new high or low never sits outside its own range.
        yearHigh() {
            const values = [this.metricData?._52WeekHigh, this.price?.highPrice].filter(isNum).map(Number);
            return values.length ? Math.max(...values) : null;
        },
        yearLow() {
            const values = [this.metricData?._52WeekLow, this.price?.lowPrice].filter((v) => isNum(v) && Number(v) > 0).map(Number);
            return values.length ? Math.min(...values) : null;
        },
        yearPos() {
            return this.fmt.rangePosition(this.price?.currentPrice, this.yearLow, this.yearHigh);
        },
        currency() {
            return this.headerData?.currency || "USD";
        },
        // The scanner sends "N/A" when no expiration qualifies.
        hasBestExpiration() {
            return /^\d{4}-\d{2}-\d{2}/.test(String(this.oss?.bestExpiration || ""));
        },
        hasOss() {
            return this.oss && isNum(this.oss.oss);
        },
        ossColor() {
            const n = Number(this.oss?.oss || 0);
            if (n >= 80) return "stroke-brand";
            if (n >= 60) return "stroke-amber";
            return "stroke-muted";
        },
        ossLabel() {
            const n = Number(this.oss?.oss || 0);
            if (n >= 80) return "In the sweet spot";
            if (n >= 60) return "Approaching";
            return "Outside the sweet spot";
        },
        gaugeCircumference() {
            return CIRCUMFERENCE;
        },
        gaugeOffset() {
            const n = Math.max(0, Math.min(100, Number(this.oss?.oss || 0)));
            return CIRCUMFERENCE * (1 - n / 100);
        },
    },
    watch: {
        "headerData.lgo"() {
            this.logoFailed = false;
        },
    },
};
</script>
